import { measure } from '@/components/Analytics/Analytics';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import SearchButton from '@/components/Search/SearchButton';
import TopOverlayPattern from '@/components/TopOverlayPattern/TopOverlayPattern';
import useCards from '@/hooks/cards/useCards';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages, {
	useNativeMessageCallback,
} from '@/hooks/native/useNativeMessages';
import useSpecies from '@/hooks/species/useSpecies';
import cn from '@/lib/cs';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import CameraFrame from './CameraFrame.svg';
import styles from './CameraRoute.module.sass';
import CameraTip from './CameraTip';
import ScanningAnimation from './ScanningAnimation';
import SpeciesPicker from './SpeciesPicker';
import ZoomAndFocusControls from './ZoomAndFocusControls';

const CameraRoute = () => {
	const navigate = useNavigate();
	const { identifyAndStore } = useCards();
	const { findSpeciesById, getSpeciesRouteById } = useSpecies();

	const isNative = useIsNative();
	const webCamRef = useRef<Webcam>(null);

	// Get from which species the camera was opened, using the state given by the navigate function
	const cameraOpenedFromSpeciesId = useLocation().state?.speciesId;
	const [targetSpeciesId, setTargetSpeciesId] = useState<number | null>(
		cameraOpenedFromSpeciesId,
	);
	if (targetSpeciesId === undefined)
		console.error('cameraOpenedFromSpeciesId is undefined');
	// Check if cameraOpenedFromSpeciesId updates
	useEffect(() => {
		if (cameraOpenedFromSpeciesId)
			setTargetSpeciesId(cameraOpenedFromSpeciesId);
	}, [cameraOpenedFromSpeciesId]);

	const [isCameraReady, setIsCameraReady] = useState(false);
	const [isZoomAvailable, setIsZoomAvailable] = useState(isNative);
	const [webVideoTrack, setWebVideoTrack] = useState<MediaStreamTrack | null>(
		null,
	);
	const [isTakingImage, setIsTakingImage] = useState(false);
	const [isCheckingImage, setIsCheckingImage] = useState(false);
	const [snapshot, setSnapshot] = useState<string | null>(null);
	const [image, setImage] = useState<string | null>(null);

	// Upload the image to the server when the image is set
	const checkImage = useCallback(
		(image: string, snapshot: string | null) => {
			if (image && targetSpeciesId && !isCheckingImage) {
				measure(
					`camera-upload-${targetSpeciesId}-${
						findSpeciesById(targetSpeciesId)?.attributes.slug
					}`,
				);
				setImage(image);
				setIsTakingImage(false);
				setIsCheckingImage(true);
				identifyAndStore(
					image,
					'photo',
					targetSpeciesId,
					false,
					[snapshot].filter(Boolean) as string[],
				).then((result) => {
					// Reset the state
					setImage(null);
					setSnapshot(null);
					setIsCheckingImage(false);

					// If the light is green, store the photo and navigate back
					if (result?.is_stored) {
						measure(
							`camera-identified-success-${
								result.algorithm_step
							}-${targetSpeciesId}-${
								findSpeciesById(targetSpeciesId)?.attributes
									.slug
							}`,
						);

						// If the previousLocationPathname is for the right species, navigate back to it
						if (
							getSpeciesRouteById(cameraOpenedFromSpeciesId) ===
							getSpeciesRouteById(result.species_id)
						) {
							navigate(-1);
						} else {
							// Else replace the current location with the species
							navigate(getSpeciesRouteById(result.species_id), {
								replace: true,
							});
						}
					} else if (result?.identified_as) {
						measure(
							`camera-identified-error-${targetSpeciesId}-${
								findSpeciesById(targetSpeciesId)?.attributes
									.slug
							}`,
						);

						// Notify the user of error
						navigate('/oeps', {
							state: {
								image: image,
								targetSpeciesId: targetSpeciesId,
								resultIdentifiedAsId: result.species_id,
								resultIdentifiedAsDisplayName:
									result.identified_as,
							},
						});
					} else {
						measure('camera-identify-error-connection');

						// Notify the user of error (probably connection error)
						navigate('/oeps', {
							state: {
								image: image,
								targetSpeciesId: targetSpeciesId,
							},
						});
					}
				});
			}
		},
		[
			targetSpeciesId,
			isCheckingImage,
			findSpeciesById,
			identifyAndStore,
			getSpeciesRouteById,
			cameraOpenedFromSpeciesId,
			navigate,
		],
	);

	const onNativeMessage = useNativeMessageCallback(
		(key, value) => {
			switch (key) {
				case 'camera.ready':
					if (value === 'true') {
						setIsCameraReady(true);
					}
					break;
				case 'camera.snapshotTaken':
					if (value) {
						setSnapshot(value);
					}
					break;
				case 'camera.pictureTaken':
					if (value) {
						checkImage(value, snapshot);
					}
					break;
				default:
					break;
			}
		},
		[checkImage],
	);
	const { sendNativeMessage } = useNativeMessages(onNativeMessage, [
		'camera.ready',
		'camera.pictureTaken',
		'camera.snapshotTaken',
	]);

	// Open native camera when the component is mounted
	useEffect(() => {
		if (isNative) {
			sendNativeMessage('camera.show', 'true');
		}
	}, [isNative, sendNativeMessage]);

	// Check if zoom is available for webVideoTrack
	useEffect(() => {
		if (webVideoTrack) {
			setIsZoomAvailable('zoom' in webVideoTrack.getCapabilities());
		}
	}, [webVideoTrack]);

	const updateZoom = useCallback(
		(newZoom: number) => {
			if (!isCameraReady) return;
			// Send zoom factor to the native camera
			if (isNative) {
				sendNativeMessage(
					'camera.setZoom',
					`${Math.round(newZoom * 1000) / 1000}`,
				);
			}
			// Set the zoom factor for the web camera
			else if (webVideoTrack && isZoomAvailable) {
				const capabilities = webVideoTrack.getCapabilities();
				if ('zoom' in capabilities) {
					const { min: minZoom, max: maxZoom } =
						capabilities.zoom as {
							min: number;
							max: number;
						};
					const zoom = minZoom + newZoom * (maxZoom - minZoom);
					// @ts-expect-error zoom is not in the types
					webVideoTrack.applyConstraints({ advanced: [{ zoom }] });
				}
			}
		},
		[
			isCameraReady,
			isNative,
			isZoomAvailable,
			sendNativeMessage,
			webVideoTrack,
		],
	);

	// Capture the image on web or native
	const capture = useCallback(
		(id: number) => {
			setTargetSpeciesId(id);
			setIsTakingImage(true);
			if (isNative) sendNativeMessage('camera.takePicture');
			else {
				const screenshot = webCamRef.current?.getScreenshot();
				setSnapshot(screenshot ?? null);
				setTimeout(() => {
					const photo = webCamRef.current?.getScreenshot();
					if (photo && screenshot) checkImage(photo, screenshot);
				}, 150);
			}
		},
		[checkImage, isNative, sendNativeMessage],
	);

	// Get target species camera tip, if available
	const targetSpeciesCameraTip = useMemo(() => {
		if (!targetSpeciesId) return undefined;
		return findSpeciesById(targetSpeciesId)?.attributes.cameraTip;
	}, [findSpeciesById, targetSpeciesId]);

	return (
		<motion.div
			className="h-dvh w-dvw overflow-hidden"
			initial={{
				background: 'black',
			}}
			animate={{
				background: isNative ? 'transparent' : 'black',
				transition: {
					duration: 1,
				},
			}}>
			<motion.main
				className="size-full text-gray-50"
				initial={{ opacity: 0, scale: '125%' }}
				animate={{
					opacity: 1,
					scale: '100%',
					transition: { duration: 0.6, ease: 'anticipate' },
				}}>
				<nav className="fixed inset-x-0 top-0 z-20 flex flex-col items-center justify-center px-11 pt-1">
					<TopOverlayPattern className="absolute inset-0 h-16 w-full -rotate-3 scale-150 text-overlay opacity-50" />

					{/* Show the target species camera tip if it has one */}
					<CameraTip
						key={targetSpeciesCameraTip}
						tip={targetSpeciesCameraTip}
					/>

					<IconButton
						className="absolute start-2 top-2 lg:start-4 lg:top-4"
						onClick={() => {
							const species = findSpeciesById(
								targetSpeciesId ?? undefined,
							);
							if (targetSpeciesId && species)
								measure(
									`camera-back-${targetSpeciesId}-${species.attributes.slug}`,
									'exit',
								);
							navigate(-1);
						}}
						aria-label="Terug"
						title="Terug"
						icon={<ArrowLeftIcon />}
					/>

					{/* Show search after delay to make it grab more attention */}
					{isCameraReady && (
						<SearchButton
							isSearchComponentMissing
							searchResultTarget="camera"
							onClickMeasure={() =>
								measure('search-open-camera', 'navigation')
							}
						/>
					)}

					<div
						className={cn(
							'absolute top-4 transition-all duration-500',
							targetSpeciesCameraTip
								? 'opacity-0 delay-1000'
								: 'opacity-100',
						)}>
						<h1 className="relative px-4 pb-1 pt-2 font-brand text-xl xxs:text-4xl">
							<LabelBackground className="text-gray-950" />
							<span className="relative">Camera</span>
						</h1>
					</div>
				</nav>
				<div className={styles.CameraRoute__Frame}>
					{/** Fade in camera when ready */}
					<motion.div
						className="absolute inset-0 scale-[0.99] bg-black"
						initial={{ opacity: 1 }}
						animate={{
							opacity: isCameraReady ? 0 : 1,
							transition: {
								delay: 0.5,
							},
						}}
					/>
					{/** Show webcam on web */}
					{!isNative && (
						<>
							<div className="absolute inset-0 scale-[0.99] bg-black" />
							<Webcam
								ref={webCamRef}
								audio={false}
								disablePictureInPicture
								onUserMediaError={(error) => {
									alert(
										'Er is een fout opgetreden. De camera kon niet worden geopend. Als dit vaker gebeurd, kan je om hulp vragen van een volwassene.\n\n' +
											error,
									);
									navigate(-1);
								}}
								screenshotFormat="image/jpeg"
								videoConstraints={{
									facingMode: 'environment',
									width: { ideal: 2160 },
									height: { ideal: 4096 },
								}}
								onUserMedia={(stream) => {
									setWebVideoTrack(
										stream.getVideoTracks()[0],
									);
									setIsCameraReady(true);
								}}
								className="pointer-events-none absolute inset-0 size-full object-cover"
							/>
						</>
					)}
					{/** User photo */}
					{(!!snapshot || !!image) && (
						<div className="size-full overflow-hidden">
							<div
								className={cn(
									'relative size-full anim-fade-in filter transition-all duration-300 bg-black',
									image
										? 'blur-0 scale-100'
										: 'blur-[1vh] scale-110',
								)}>
								<img
									alt="Jouw gemaakte foto"
									src={image ?? snapshot ?? ''}
									className="pointer-events-none absolute inset-0 size-full object-cover"
								/>
								{!!image && <ScanningAnimation />}
							</div>
						</div>
					)}

					{/* UI frame */}
					<div className={styles.CameraRoute__Frame__OverlayTop} />
					<div className={styles.CameraRoute__Frame__OverlayBottom} />
					<div className={styles.CameraRoute__Frame__OverlayLeft} />
					<div className={styles.CameraRoute__Frame__OverlayRight} />
					<img
						alt=""
						src={CameraFrame}
						className="pointer-events-none"
					/>

					{/* Zoom and focus controls */}
					{!image && !isCheckingImage && isZoomAvailable && (
						<ZoomAndFocusControls updateZoom={updateZoom} />
					)}
				</div>
				{!image && (
					<SpeciesPicker
						key={cameraOpenedFromSpeciesId}
						capture={capture}
						defaultSpeciesId={cameraOpenedFromSpeciesId}
						setCameraTargetSpeciesId={setTargetSpeciesId}
					/>
				)}

				{(isTakingImage || isCheckingImage) && (
					<audio
						src={`/assets/sounds/camera_${isTakingImage ? 'shutter' : 'loading'}.mp3`}
						autoPlay
						aria-hidden
						className="hidden"
					/>
				)}
			</motion.main>
		</motion.div>
	);
};

export default CameraRoute;
